import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, ArrowRight, Check, Copy, Download, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { confettiBurst } from "@/lib/confetti";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthenticatorAppsHint } from "@/components/settings/AuthenticatorApps";

/** Ten one-time codes, shown once: copy, download, and a tick that they were saved before moving on */
export function BackupCodes({ codes, onDone, doneLabel = "Done" }: { codes: string[]; onDone: () => void; doneLabel?: string }) {
  const { success, error } = useToast();
  const [saved, setSaved] = useState(false);
  const text = codes.join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      success("Copied", "Paste them somewhere safe, like a password manager");
    } catch {
      error("Could not copy", "Select the codes and copy them by hand");
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`Nelgates Sales backup codes\nEach code works once.\n\n${text}\n`], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "nelgates-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Save your backup codes</h3>
        <p className="text-xs text-muted-foreground">
          If you lose your phone, each of these codes signs you in once. They are shown only now, so keep them somewhere safe.
        </p>
      </div>
      <ul aria-label="Backup codes" className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg border border-border bg-muted/50 p-3.5 font-mono text-[13px] tracking-wider text-foreground sm:max-w-sm">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={copy}>
          <Copy className="mr-1.5 size-3.5" />
          Copy
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={download}>
          <Download className="mr-1.5 size-3.5" />
          Download
        </Button>
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-[13px] text-foreground">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="size-4 cursor-pointer accent-primary" />
        I have saved these codes
      </label>
      <Button type="button" onClick={onDone} disabled={!saved}>
        {doneLabel}
      </Button>
    </div>
  );
}

/** A manual key in groups of four, easier to type from a screen */
const grouped = (key: string) => key.replace(/(.{4})/g, "$1 ").trim();

const SETUP_STEPS = ["Scan the code", "Enter the code", "Save backup codes"];

/** "Step 2 of 3" with a three-part bar, above each screen of the set-up */
function SetupProgress({ stage }: { stage: number }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Step {stage + 1} of {SETUP_STEPS.length} · <span className="text-foreground">{SETUP_STEPS[stage]}</span>
      </p>
      <div className="grid grid-cols-3 gap-1.5" aria-hidden="true">
        {SETUP_STEPS.map((label, i) => (
          <span key={label} className={cn("h-1 rounded-full transition-colors duration-300", i <= stage ? "bg-primary" : "bg-border")} />
        ))}
      </div>
    </div>
  );
}

type PendingSetup = { secret: string; otpauth_uri: string };

/**
 * Setting up two-step verification, one screen at a time: scan the QR code (or type the key), confirm with the first
 * code, then save the backup codes. It starts the set-up as soon as it is shown; nothing is saved until the code is
 * confirmed. onStageChange tells the page which of the three screens is showing.
 * Moving to a new phone passes the QR code it already got (`initial`), where to confirm, and what to do if it expires.
 */
export function TwoFactorSetup({
  onDone,
  onCancel,
  onStageChange,
  initial,
  confirmPath = "/api/auth/2fa/enable",
  onExpired,
  doneTitle = "Two-step verification is on",
}: {
  onDone: () => void;
  onCancel?: () => void;
  onStageChange?: (stage: number) => void;
  initial?: PendingSetup;
  confirmPath?: string;
  onExpired?: (message: string) => void;
  doneTitle?: string;
}) {
  const [setup, setSetup] = useState<PendingSetup | null>(initial ?? null);
  const [failed, setFailed] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState<string[] | null>(null);
  const stage = backup ? 2 : scanned ? 1 : 0;

  useEffect(() => {
    onStageChange?.(stage);
  }, [stage]);

  const start = async () => {
    setFailed(null);
    try {
      setSetup(unwrapObject<PendingSetup>(await api.post("/api/auth/2fa/setup", {})));
    } catch (err: any) {
      setFailed(err?.message || "Could not start the set-up. Please try again.");
    }
  };
  useEffect(() => {
    if (!initial) start();
  }, []);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = unwrapObject<{ backup_codes: string[] }>(await api.post(confirmPath, { code: code.trim() }));
      setBackup(res?.backup_codes ?? []);
      confettiBurst();
    } catch (err: any) {
      const message = err?.message || "That code is not right. Check the app and try again.";
      setCode("");
      // The set-up expired: a new QR code is needed, so go back to scanning it
      if (/expired/i.test(message)) {
        if (onExpired) return onExpired(message);
        setScanned(false);
        start();
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  if (backup) {
    return (
      <div className="space-y-5">
        <SetupProgress stage={2} />
        <div role="status" className="flex items-center gap-3 rounded-xl border border-success/30 bg-success-soft px-3.5 py-3">
          <span className="auth-pop flex size-9 shrink-0 items-center justify-center rounded-full bg-success text-white shadow-md shadow-success/30">
            <Check className="size-5" strokeWidth={3} />
          </span>
          <div className="auth-fade" style={{ animationDelay: "0.2s" }}>
            <p className="text-sm font-semibold text-foreground">{doneTitle}</p>
            <p className="text-xs text-muted-foreground">One last thing: keep your backup codes safe.</p>
          </div>
        </div>
        <BackupCodes codes={backup} onDone={onDone} />
      </div>
    );
  }

  if (failed) {
    return (
      <p role="alert" className="text-sm text-danger">
        {failed} <button type="button" onClick={start} className="font-medium underline">Try again</button>
      </p>
    );
  }
  if (!setup) {
    return (
      <div role="status" className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Getting your code ready…
      </div>
    );
  }

  const cancel = onCancel && (
    <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
      Cancel
    </Button>
  );

  if (!scanned) {
    return (
      <div className="space-y-5">
        <SetupProgress stage={0} />
        <p className="text-[13px] text-foreground">
          Open an authenticator app <AuthenticatorAppsHint /> on your phone and scan this code.
        </p>
        <div className="w-fit rounded-lg border border-border bg-white p-2.5">
          <QRCodeSVG value={setup.otpauth_uri} size={176} title="QR code for your authenticator app" />
        </div>
        {error && <p role="alert" className="text-xs text-danger">{error} Scan the new code.</p>}
        <div className="text-xs text-muted-foreground">
          Cannot scan it? Choose “enter a key” in the app and type:
          <p className="mt-1 break-all font-mono text-[13px] tracking-wider text-foreground">{grouped(setup.secret)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => {
              setError(null);
              setScanned(true);
            }}
          >
            I’ve scanned it
            <ArrowRight className="ml-1.5 size-4" />
          </Button>
          {cancel}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={confirm} className="space-y-5">
      <SetupProgress stage={1} />
      <div className="space-y-1.5">
        <Label htmlFor="two-factor-code" className="text-[13px]">
          Enter the 6-digit code the app shows for Nelgates Sales
        </Label>
        <Input
          id="two-factor-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={7}
          placeholder="123 456"
          className="h-11 max-w-[220px] font-mono text-lg tracking-[0.25em]"
          aria-invalid={error ? true : undefined}
        />
        {error ? (
          <p role="alert" className="text-xs text-danger">{error}</p>
        ) : (
          <p className="text-xs text-muted-foreground">The code changes every 30 seconds; type the one showing now.</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => setScanned(false)} disabled={busy}>
          <ArrowLeft className="mr-1.5 size-4" />
          Back
        </Button>
        <Button type="submit" disabled={busy || code.replace(/\s/g, "").length < 6}>
          {busy ? (
            <>
              <Loader2 className="mr-1.5 size-4 animate-spin" />
              Checking…
            </>
          ) : (
            <>
              <Check className="mr-1.5 size-4" />
              Turn on
            </>
          )}
        </Button>
        {cancel}
      </div>
    </form>
  );
}
